import { PaginationSchema } from "@/components/paginate/types";
import { z } from "zod";

// Cost Attribution Mode enum
export enum CostAttributionMode {
  Query = "query",
  Warehouse = "warehouse",
  Workspace = "workspace",
  CustomTag = "custom-tag",
}

export { tenantInfoSchema, type GetTenantInfoResponse } from "./tenantInfoSchema";

export const urlResponse = z.object({
  url: z.string(),
});

export type UrlResponse = z.infer<typeof urlResponse>;

export const FilterZodSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const GetOpportunitiesDynamicFilterResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(FilterZodSchema).nullable(),
});

export type GetOpportunitiesDynamicFilterResponse = z.infer<
  typeof GetOpportunitiesDynamicFilterResponseSchema
>;

export const GetTableLastRefreshedResponseSchema = z.object({
  last_refreshed: z.string().nullable(),
});

export type GetTableLastRefreshedResponse = z.infer<typeof GetTableLastRefreshedResponseSchema>;

export const TableRkFilterResponseSchema = z.object({
  items: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      database: z.string().optional().nullable(),
      table_schema: z.string().optional().nullable(),
    })
  ),
  total: z.number(),
  page: z.number(),
  size: z.number(),
  pages: z.number(),
  filter_type: z.string().optional(),
});

export type TableRkFilterResponse = z.infer<typeof TableRkFilterResponseSchema>;

export const permissionsFullSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  title: z.string(),
  description: z.string(),
  group: z.string().nullable(),
});

export const rolePermissionFullSchema = z.object({
  role_name: z.string(),
  role_description: z.string(),
  role_title: z.string(),
  is_custom: z.boolean(),
  permission: z.array(permissionsFullSchema),
});

export const userLoginOutSchema = z.object({
  id: z.number().nullable(),
  email: z.string(),
  auth_token: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  is_onboarded: z.boolean(),
  tz: z.string().nullable(),
  lang: z.string().nullable(),
  uuid: z.string(),
  role_FK: rolePermissionFullSchema.nullable(),
});

export type UserLoginOutResponse = z.infer<typeof userLoginOutSchema>;

export const StandardResponseSchema = z.object({
  ok: z.boolean(),
});

export type StandardResponse = z.infer<typeof StandardResponseSchema>;

export const StandardResponseWithWarningSchema = z.object({
  ok: z.boolean(),
  warning: z.string().optional(),
});

export type StandardResponseWithWarning = z.infer<typeof StandardResponseWithWarningSchema>;

export const pageSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({ items: z.array(itemSchema), ...PaginationSchema.shape });

// POST /studio/session_to_email_mailing_list returns this, not { ok }.
export const SessionEmailResponseSchema = z.object({
  success: z.boolean(),
  emails_sent: z.number(),
  message: z.string(),
});

export type SessionEmailResponse = z.infer<typeof SessionEmailResponseSchema>;

export const CompanySchema = z.object({
  name: z.string(),
});

export const CompanyListItemSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  country: z.string().nullable(),
  provider: z.string().nullish(),
});

export const CompanyListResponseSchema = pageSchema(CompanyListItemSchema);

// Mirrors the backend CompanyIndexResponse returned by POST /companies/: the
// created company resource, not a generic { ok } acknowledgement.
export const CompanyCreateResponseSchema = z.object({
  uuid: z.string(),
  name: z.string().nullish(),
  country: z.string().nullish(),
  created_at: z.string().nullish(),
  existence_time: z.string().nullish(),
  provider: z.string().nullish(),
});

export const CompanyInfoResponseSchema = z.object({
  other_tenants_in_group: z.array(CompanySchema),
});

export type Company = z.infer<typeof CompanySchema>;
export type CompanyListItem = z.infer<typeof CompanyListItemSchema>;
export type CompanyListResponse = z.infer<typeof CompanyListResponseSchema>;
export type CompanyCreateResponse = z.infer<typeof CompanyCreateResponseSchema>;
export type CompanyInfoResponse = z.infer<typeof CompanyInfoResponseSchema>;

export const demoSessionResponseSchema = z.object({ demoUrl: z.string(), sessionId: z.string() });

export type DemoSessionResponse = z.infer<typeof demoSessionResponseSchema>;

// Email instance list response schema
export const EmailInstanceSchema = z.object({
  company: z.object({
    name: z.string(),
    existence_time: z.string().optional(),
  }),
  company_url: z.string(),
});

export const EmailInstanceListResponseSchema = z.object({
  companies: z.array(EmailInstanceSchema).nullable(),
});

export type EmailInstance = z.infer<typeof EmailInstanceSchema>;
export type EmailInstanceListResponse = z.infer<typeof EmailInstanceListResponseSchema>;
